import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, BookOpen, Filter } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = ["All", "Primary TET", "Upper Primary TET", "C-TET", "Group C", "Group D", "General"];

export default function BooksPage() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("latest");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sort]);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ content_type: "book" });
      if (category !== "All") params.append("category", category);
      if (sort) params.append("sort", sort);
      const { data } = await api.get(`/courses?${params.toString()}`);
      setBooks(data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const filtered = search
    ? books.filter((b) => b.title.toLowerCase().includes(search.toLowerCase()))
    : books;

  return (
    <div className="page-with-nav bg-white min-h-screen" data-testid="books-page">
      {/* Header */}
      <div className="bg-slate-900 px-4 pt-4 pb-5 text-white">
        <h1 className="text-xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Books & Study Materials
        </h1>
        <p className="text-xs text-slate-400 mt-0.5">PDF Books, Notes & E-Books for competitive exams</p>
        {/* Search */}
        <div className="relative mt-3">
          <Search className="absolute left-3 top-3 text-slate-500" size={16} />
          <input
            data-testid="books-search"
            type="text"
            placeholder="Search books..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-slate-800 border-b-2 border-slate-700 pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#0284C7] transition-none"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-50 px-4 py-3 space-y-2">
        <div className="flex overflow-x-auto gap-2 scrollbar-hide">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`flex-shrink-0 text-xs font-bold px-3 py-2 transition-none ${
                category === cat ? "bg-[#0284C7] text-white" : "bg-white text-slate-600 border border-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="w-full bg-white border border-slate-200 text-xs px-3 py-2 focus:outline-none"
        >
          <option value="latest">Latest</option>
          <option value="price_low">Price: Low to High</option>
          <option value="price_high">Price: High to Low</option>
        </select>
      </div>

      {/* Results */}
      <div className="px-4 py-4">
        <p className="text-xs text-slate-500 mb-3">{filtered.length} books found</p>
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">No books found</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="books-list">
            {filtered.map((book) => {
              const discount = book.mrp > book.price ? Math.round(((book.mrp - book.price) / book.mrp) * 100) : 0;
              return (
                <div
                  key={book.id}
                  data-testid={`book-card-${book.id}`}
                  onClick={() => navigate(`/courses/${book.id}`)}
                  className="flex gap-3 bg-white cursor-pointer border-b border-slate-100 pb-3"
                >
                  <div className="w-28 aspect-[3/4] bg-slate-200 flex-shrink-0 overflow-hidden">
                    <img
                      src={book.image_url || "https://images.unsplash.com/photo-1622704029696-60ef9d5f9d55?w=300&h=400&fit=crop"}
                      alt={book.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-between py-1">
                    <div>
                      <span className="text-[10px] font-bold uppercase text-[#0284C7]">{book.category}</span>
                      <h3 className="text-sm font-bold text-slate-900 mt-0.5 line-clamp-2">{book.title}</h3>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{book.description}</p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-base text-slate-900">&#8377;{book.price}</span>
                        {book.mrp > book.price && (
                          <>
                            <span className="text-xs text-slate-400 line-through">&#8377;{book.mrp}</span>
                            <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5">{discount}%</span>
                          </>
                        )}
                      </div>
                      <span className="bg-[#0284C7] text-white text-[10px] font-bold px-3 py-1.5">BUY NOW</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
